import { Table } from "nazare-ui/components";
import type { TableProps } from "nazare-ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "nazare-ui/primitives";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, type RefObject } from "react";

import {
  presentInboxAnalyticsCell,
  type InboxAnalyticsTableBucket,
  type InboxAnalyticsTableRow,
} from "./inbox-analytics-table";
import type { InboxAnalyticsSort } from "./inbox-analytics-url-state";
import styles from "./routes/inboxes.module.css";

const INBOX_COLUMN_WIDTH = 240;
const BUCKET_COLUMN_WIDTH = 160;

function SortButton({
  label,
  sortKey,
  activeSort,
  direction,
  onSort,
}: {
  label: string;
  sortKey: InboxAnalyticsSort;
  activeSort: InboxAnalyticsSort;
  direction: "asc" | "desc";
  onSort: (sortKey: InboxAnalyticsSort) => void;
}) {
  const active = activeSort === sortKey;
  return (
    <button
      type="button"
      className={styles.sortButton}
      aria-label={`Sort by ${label}${active ? `, ${direction === "asc" ? "ascending" : "descending"}` : ""}`}
      aria-pressed={active}
      onClick={(event) => {
        event.stopPropagation();
        onSort(sortKey);
      }}
    >
      {label}
      {active ? (
        direction === "asc" ? <ArrowUp aria-hidden="true" className={styles.sortIcon} /> : <ArrowDown aria-hidden="true" className={styles.sortIcon} />
      ) : (
        <ArrowUpDown aria-hidden="true" className={styles.sortIcon} />
      )}
    </button>
  );
}

function MetricCell({
  inboxAddress,
  bucket,
  cell,
}: {
  inboxAddress: string;
  bucket: InboxAnalyticsTableBucket;
  cell: InboxAnalyticsTableRow["cells"][number];
}) {
  const presentation = presentInboxAnalyticsCell(
    inboxAddress,
    bucket.label,
    cell,
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={styles.metricCell}
          aria-label={`${inboxAddress}, ${bucket.label}: ${presentation.tooltip}`}
        >
          <span className={styles.primaryCount}>{presentation.primary}</span>
          {presentation.legacy !== null && (
            <span className={styles.legacyCount}>{presentation.legacy}</span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent sideOffset={6}>
        {presentation.tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function InboxAnalyticsTable({
  rows,
  buckets,
  sort,
  direction,
  onSort,
  scrollContainerRef,
}: {
  rows: InboxAnalyticsTableRow[];
  buckets: InboxAnalyticsTableBucket[];
  sort: InboxAnalyticsSort;
  direction: "asc" | "desc";
  onSort: (sortKey: InboxAnalyticsSort) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}) {
  const columns = useMemo<
    TableProps<InboxAnalyticsTableRow>["columns"]
  >(
    () => [
      {
        id: "inbox",
        accessorFn: (row) => row.inbox.inboxName,
        header: () => (
          <SortButton
            label="Inbox"
            sortKey="inbox"
            activeSort={sort}
            direction={direction}
            onSort={onSort}
          />
        ),
        enableSorting: false,
        size: INBOX_COLUMN_WIDTH,
        meta: {
          gridSize: `${INBOX_COLUMN_WIDTH}px`,
          sticky: "left",
          align: "left" as const,
        },
        cell: ({ row }) => (
          <span className={styles.inboxName}>{row.original.inbox.inboxName}</span>
        ),
      },
      ...buckets.map((bucket, bucketIndex) => ({
        id: bucket.bucketStart,
        accessorFn: (row) => row.cells[bucketIndex]?.gmail ?? 0,
        header: () => (
          <SortButton
            label={bucket.label}
            sortKey={bucket.bucketStart}
            activeSort={sort}
            direction={direction}
            onSort={onSort}
          />
        ),
        enableSorting: false,
        size: BUCKET_COLUMN_WIDTH,
        meta: { grow: true, align: "right" as const },
        cell: ({ row }) => (
          <MetricCell
            inboxAddress={row.original.inbox.inboxName}
            bucket={bucket}
            cell={row.original.cells[bucketIndex] ?? { gmail: 0, legacy: 0 }}
          />
        ),
      })),
    ],
    [buckets, direction, onSort, sort],
  );

  return (
    <Table
      columns={columns}
      defaultData={rows}
      syncData
      virtual={{ columns: true, rows: true }}
      columnGap={0}
      scrollContainerRef={scrollContainerRef}
    />
  );
}
